//products-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import { IconButton, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Page from "../components/page";
import { productsApi } from "../services/api/products";
import LoadingBackdrop from "../components/loading-backdrop";
import { Product } from "../models/product";
import ConfirmDialog from "../components/confirm-dialog";
import { ErrorContext, UserContext } from "../components/app-routes";
import { ActiveFilterControls } from "../components/active-filter-controls";
import mixpanel from "mixpanel-browser";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { getCustomLabel } from "../utils/custom-labels";

const columns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: "Checkbox",
  },
  {
    field: "archived",
    headerName: "",
    width: 35,
    minWidth: 35,
    sortable: false,
    filterable: false,
    align: "center",
    disableColumnMenu: true,
    disableExport: true,
    renderCell: (params) =>
      params.row.archivedAt ? (
        <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
      ) : null,
  },
  {
    field: "name",
    headerName: "Name",
    width: 400,
  },
  {
    field: "crops",
    headerName: "Crops",
    flex: 1,
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 200,
  },
];

const ProductsDataGrid = React.memo(
  (props: {
    initialPage: number | undefined;
    onPaginationModelChange: (model: any) => void;
    products: Product[] | null | undefined;
    onSelectionChange: (params: GridRowSelectionModel) => void;
  }) => {
    const history = useHistory();
    const context = useContext(UserContext);
    const grower = context.grower;

    if (!grower) {
      throw new Error("Invalid context");
    }

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Products List - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    if (!props.products) {
      return null;
    }

    return (
      <StripedDataGrid
        storageKey="products-grid"
        columns={columns}
        rows={props.products.map((p) => ({
          id: p.id,
          name: p.name,
          crops: p.productCrops.map((pC) => pC.crop.name).join(),
          notes: p.notes,
          archivedAt: p.archivedAt,
        }))}
        initialState={{
          columns: {
            columnVisibilityModel: {
              notes: false,
            },
          },
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
          pagination: {
            paginationModel: {
              page: props.initialPage ?? 0,
              pageSize: 15,
            },
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        slots={{
          toolbar: GridToolbar,
        }}
        showToolbar
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) =>
          history.push(`/home/products/${params.id}`)
        }
      />
    );
  },
);

export default function Products() {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [totalTrashed, setTotalTrashed] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const showArchivedCheckedStorageKey = "productsShowArchivedChecked";
  const showActiveCheckedStorageKey = "productsShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );
  const history = useHistory();
  const isMounted = useRef(true);

  useEffect(() => {
    async function getProducts() {
      setBusy(true);
      try {
        setProducts(await productsApi.getAll());
      } catch (error) {
        handleError(error);
      } finally {
        setBusy(false);
      }
    }
    getProducts();
  }, [totalTrashed]);

  const handleSelectionChange = (m: GridRowSelectionModel): void => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids);
  };

  const handleClickTrash = () => {
    setTrashDialogOpen(true);
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setTrashDialogOpen(false);

    if (!okTrash) {
      return;
    }

    setBusy(true);

    let trashed = 0;
    for (let i = 0; i < selected.length; i++) {
      try {
        await productsApi.trash(selected[i]);
        trashed++;
        mixpanel.track("Delete Product");
      } catch (error) {
        if (isMounted.current) handleError(error);
        break;
      }
    }

    if (isMounted.current) {
      setTotalTrashed(totalTrashed + trashed);
      setBusy(false);
    }
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const context = useContext(UserContext);
  const grower = context.grower;

  return (
    <Page title={getCustomLabel(grower ?? null, "products")}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/home/products/add"
          disabled={busy}
        >
          Add
        </Button>
        <IconButton
          aria-label="delete"
          disabled={busy || selected.length === 0}
          onClick={handleClickTrash}
          size="large"
        >
          <DeleteIcon />
        </IconButton>

        <ActiveFilterControls
          showActiveChecked={showActiveChecked}
          showArchivedChecked={showArchivedChecked}
          onChangeShowActive={handleShowActiveChanged}
          onChangeShowArchived={handleShowArchivedChanged}
        />
      </Box>
      <Box sx={{ height: 750, width: "100%", pt: 2 }}>
        <ProductsDataGrid
          products={products?.filter(
            (p) =>
              (showActiveChecked && p.archivedAt === null) ||
              (showArchivedChecked && p.archivedAt !== null),
          )}
          onSelectionChange={handleSelectionChange}
          initialPage={getSearchParamNumber(history, "page", 0)}
          onPaginationModelChange={(m) => {
            const params = new URLSearchParams(`page=${m.page}`);
            history.replace({
              search: params.toString(),
            });
          }}
        />
      </Box>
      <ConfirmDialog
        title="Trash Products"
        description={`Are you sure you want to trash ${
          selected.length
        } product${selected.length > 1 ? "s" : ""}?`}
        open={trashDialogOpen}
        onClose={handleTrashDialogClose}
      ></ConfirmDialog>
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
